import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  TextField,
  Paper,
  IconButton,
  CircularProgress,
  Divider,
  InputBase,
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import useAppStore from 'store/useAppStore';
import api from 'services/api';
import { produce } from 'immer';
import { DateTime } from 'luxon';
import { getScript } from 'utils/getScript';
import HistoryArea from './HistoryArea';

function ScriptEditorDialogChild({ onClose, scriptIndex }) {
  const script = useAppStore((state) => state.scripts[scriptIndex]);
  const project_id = useAppStore((state) => state.selectedProject.project_id);
  const user_id = useAppStore((state) => state.user.id);
  const comp_name = useAppStore((state) => state.user.company);
  const seg_script_id = script.id;

  const [save2DBSegScript, { isLoading: isSave2DBSegScriptLoading }] =
    api.endpoints.save2DBSegScript.useMutation();
  const [segScriptChat, { isLoading: isSegScriptChatLoading }] =
    api.endpoints.segScriptChat.useMutation();

  const setter = useAppStore((state) => state.setter);
  const showNotification = useAppStore((state) => state.showNotification);
  const [editedScript, setEditedScript] = useState(script);

  const [chatMessages, setChatMessages] = useState(script.chat || []);
  const [newMessage, setNewMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const chatMessagesRef = React.useRef(null);

  const handleSave = async () => {
    if (!editedScript.segScript.trim()) {
      showNotification('請輸入腳本內容', 'error');
      return;
    }
    const scriptKeys = useAppStore.getState().scriptKeys;
    const seg_script_info = {};
    scriptKeys.forEach((key) => {
      seg_script_info[key] = script[key];
    });
    const seg_script_name = script['區段腳本名稱'];
    const edit_time = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss');
    save2DBSegScript({
      hand_transp: JSON.stringify(useAppStore.getState().materialDevices),
      station: JSON.stringify(useAppStore.getState().stations),
      seg_script_data: JSON.stringify({
        seg_script_id: seg_script_id,
        seg_script_info: getScript(seg_script_info),
      }),
      seg_script_name: seg_script_name,
      project_id: project_id,
      seg_script_id: seg_script_id,
      user_id: user_id,
      comp_name: comp_name,
      edit_time: edit_time,
      seg_script: editedScript.segScript,
    })
      .unwrap()
      .then(() => {
        setter('scripts', (draft) => {
          const findIndex = draft.findIndex((item) => item.id === script.id);
          if (findIndex !== -1) {
            draft[findIndex].segScript = editedScript.segScript;
          }
        });
        showNotification('腳本儲存成功！', 'success');
      })
      .catch((error) => {
        showNotification(error.message || '儲存腳本失敗', 'error');
      });
  };

  const handleInputChange = (field, value) => {
    setEditedScript((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // 自動捲動到聊天區域底部
  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) {
      return;
    }
    // 添加用戶訊息到聊天記錄
    const userMessage = {
      id: Date.now(),
      text: newMessage,
      sender: 'user',
      timestamp: new Date().toLocaleString(),
    };
    const updatedChatMessages = [...chatMessages, userMessage];
    setChatMessages(updatedChatMessages);

    // 將聊天內容存入 store 中的腳本物件
    // if (script) {
    //   updateScript(script.id, 'chat', updatedChatMessages);
    // }

    // 自動捲動到聊天區域底部
    setTimeout(scrollToBottom, 100);

    const currentMessage = newMessage;
    setNewMessage('');

    // 調用聊天 API
    segScriptChat({
      session_id: sessionId,
      project_id: useAppStore.getState().selectedProject.project_id,
      seg_script_id: seg_script_id,
      seg_script: editedScript.segScript || script?.segScript || '',
      question: currentMessage,
    })
      .unwrap()
      .then((result) => {
        setSessionId(result.session_id);
        // 添加 AI 回應到聊天記錄
        const aiMessage = {
          id: Date.now() + 1,
          text: result.reply,
          sender: 'ai',
          timestamp: new Date().toLocaleString(),
        };
        const finalChatMessages = [...updatedChatMessages, aiMessage];
        setChatMessages(finalChatMessages);

        // 將包含 AI 回應的聊天內容存入 store 中的腳本物件
        // if (script) {
        //   updateScript(script.id, 'chat', finalChatMessages);
        // }

        // 自動捲動到聊天區域底部
        setTimeout(scrollToBottom, 100);

        // 如果有優化後的腳本，直接覆蓋腳本內容
        if (result.reply_seg_script) {
          setEditedScript((prev) => ({
            ...prev,
            segScript: result.reply_seg_script,
          }));
          setter('scripts', (draft) => {
            const findIndex = draft.findIndex((item) => item.id === script.id);
            if (findIndex !== -1) {
              draft[findIndex].segScript = result.reply_seg_script;
            }
          });
          // // 同時更新 store 中的腳本內容
          // if (script) {
          //   updateScript(script.id, 'segScript', result.replySegScript);
          // }
          showNotification('腳本內容已自動更新！', 'success');
        }
      })
      .catch((error) => {
        showNotification(error.message || '聊天請求失敗', 'error');
      });
  };

  const handleRestoreVersion = useCallback((version) => {
    setEditedScript(
      produce((draft) => {
        draft.segScript = version.script.replace(/<br>/g, '\n');
      }),
    );
  }, []);

  return (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          <Typography variant="h6" component="div">
            編輯腳本: {script['區段腳本名稱'] || '未命名腳本'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box className="flex min-h-0 flex-1 pb-2">
        {/* 左側區域 - 腳本內容和歷史紀錄 */}
        <Box className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex min-h-0 flex-7 flex-col px-2">
            <div className="flex items-center justify-between py-2">
              <span className="font-bold">區段腳本內容(可編輯)</span>
              <Button
                size="small"
                loading={isSave2DBSegScriptLoading}
                onClick={handleSave}
                variant="contained"
              >
                儲存
              </Button>
            </div>
            <Paper variant="outlined" className="flex flex-1 overflow-y-auto">
              <InputBase
                className="p-2"
                sx={{
                  alignItems: 'flex-start', // 讓 flex 容器內部元素靠上
                  '& .MuiInputBase-input': {
                    height: '100%', // 填滿容器高度
                    padding: '8px', // 控制內距
                    boxSizing: 'border-box',
                    alignSelf: 'flex-start', // 文字靠上
                  },
                }}
                fullWidth
                multiline
                value={editedScript.segScript}
                onChange={(e) => handleInputChange('segScript', e.target.value)}
              />
            </Paper>
          </div>
          {/* 歷史紀錄區塊 */}
          <div className="flex min-h-0 flex-3 px-2">
            <HistoryArea
              seg_script_id={seg_script_id}
              handleRestoreVersion={handleRestoreVersion}
            />
          </div>
        </Box>

        {/* 右側聊天室區域 */}
        <Box className="flex min-w-0 flex-1 flex-col pr-2">
          <div className="p-3 font-bold">與大語言模型對話</div>
          <Paper
            className="flex min-h-0 flex-1 flex-col p-2"
            variant="outlined"
          >
            <Box
              ref={chatMessagesRef}
              className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2"
            >
              {chatMessages.map((message) => (
                <Box key={message.id} sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent:
                        message.sender === 'user' ? 'flex-end' : 'flex-start',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {message.timestamp}
                    </Typography>
                  </Box>
                  <Paper
                    sx={{
                      p: 1,
                      backgroundColor:
                        message.sender === 'user'
                          ? (theme) =>
                              theme.palette.mode === 'dark'
                                ? '#1976d2'
                                : '#e3f2fd'
                          : (theme) =>
                              theme.palette.mode === 'dark'
                                ? '#9c27b0'
                                : '#f3e5f5',
                      maxWidth: '80%',
                      ml: message.sender === 'user' ? 'auto' : 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          message.sender === 'user'
                            ? (theme) =>
                                theme.palette.mode === 'dark'
                                  ? '#ffffff'
                                  : 'text.primary'
                            : (theme) =>
                                theme.palette.mode === 'dark'
                                  ? '#ffffff'
                                  : 'text.primary',
                      }}
                    >
                      {message.text}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
            <Box
              sx={{ display: 'flex', gap: 1 }}
              component="form"
              onSubmit={handleSendMessage}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="輸入訊息..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={isSegScriptChatLoading}
              />
              <Button
                variant="contained"
                size="small"
                type="submit"
                loading={isSegScriptChatLoading}
                disabled={!newMessage.trim() || isSegScriptChatLoading}
              >
                發送
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}

const ScriptEditorDialog = ({ onClose, scriptIndex }) => {
  const readOnly = useAppStore((state) => state.selectedProject.readOnly);
  const hand_transp = useAppStore((state) => state.materialDevices);
  const setter = useAppStore((state) => state.setter);
  const stations = useAppStore((state) => state.stations);
  const ori_project_id = useAppStore(
    (state) => state.selectedProject.ori_project_id,
  );
  const project_id = useAppStore((state) => state.selectedProject.project_id);
  const user_id = useAppStore((state) => state.user.id);
  const comp_name = useAppStore((state) => state.user.company);
  const scriptKeys = useAppStore((state) => state.scriptKeys);
  const related_project_id = useAppStore(
    (state) => state.selectedRelatedProject,
  );
  const related_project_id_keys = useMemo(() => {
    const keys = Object.keys(related_project_id);
    return keys.length > 0 ? JSON.stringify(keys) : JSON.stringify([]);
  }, [related_project_id]);
  const script = useAppStore((state) => state.scripts[scriptIndex]);
  const seg_script_info = useMemo(() => {
    const data = {};
    scriptKeys.forEach((key) => {
      data[key] = script[key];
    });
    return data;
  }, [script, scriptKeys]);
  const { currentData, isFetching, isError, refetch } =
    api.endpoints.genSegScript.useQuery({
      hand_transp: JSON.stringify(hand_transp),
      station: JSON.stringify(stations),
      seg_script: JSON.stringify({
        seg_script_id: script.id,
        seg_script_info: getScript(seg_script_info),
      }),
      related_project_id: related_project_id_keys,
      ori_project_id: readOnly ? project_id : ori_project_id || '',
      user_id: user_id || '',
      comp_name: comp_name || '',
      project_id: project_id || '',
    });
  useEffect(() => {
    if (currentData) {
      setter('scripts', (draft) => {
        draft[scriptIndex].segScript = currentData.seg_script;
      });
    }
  }, [currentData, scriptIndex, setter]);
  if (!currentData && isFetching) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <CircularProgress />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex flex-col items-center p-2">
        生成腳本發生錯誤
        <Button component="span" onClick={refetch}>
          重試
        </Button>
      </div>
    );
  }
  if (readOnly) {
    return (
      <>
        <DialogTitle className="flex items-center justify-between">
          <div>腳本內容: {script['區段腳本名稱'] || '未命名腳本'}</div>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography className="whitespace-pre-wrap">
            {currentData.seg_script}
          </Typography>
        </DialogContent>
      </>
    );
  }
  if (script.segScript) {
    return (
      <ScriptEditorDialogChild scriptIndex={scriptIndex} onClose={onClose} />
    );
  }
};
export default ScriptEditorDialog;
